/**
 * PR-35 — VerseSport Global Branding
 *
 * Central source of truth for the official VerseSport slogan:
 *   ES — "Inteligencia Deportiva en Tiempo Real"
 *   EN — "Sports Intelligence in Real Time"
 *   PT — "Inteligência Esportiva em Tempo Real"
 *
 * Existing branding (product name, tagline, legal) is preserved.
 * This module only adds the slogan layer.
 */

export type SloganLang = "ES" | "EN" | "PT";

export const VERSESPORT_SLOGAN: Record<SloganLang, string> = {
  ES: "Inteligencia Deportiva en Tiempo Real",
  EN: "Sports Intelligence in Real Time",
  PT: "Inteligência Esportiva em Tempo Real",
};

/**
 * World Cup campaign tagline — used cinematically (intros, countdowns,
 * cinematic moments, exports). NOT used as a permanent UI label.
 */
export const VERSESPORT_WC_CAMPAIGN: Record<SloganLang, string> = {
  ES: "Donde el Mundial cobra vida.",
  EN: "Where the World Cup comes alive.",
  PT: "Onde a Copa do Mundo ganha vida.",
};

export function getWcCampaign(lang: SloganLang = "ES"): string {
  return VERSESPORT_WC_CAMPAIGN[lang] ?? VERSESPORT_WC_CAMPAIGN.ES;
}

/** Sections where the slogan can be toggled on/off by SuperAdmin (demo-local). */
export type SloganSection =
  | "home"
  | "login"
  | "register"
  | "onboarding"
  | "header"
  | "profile"
  | "partner_portal"
  | "war_room"
  | "splash"
  | "footer"
  | "seo"
  | "reports"
  | "emails"
  | "loading";

const STORAGE_KEY = "vs:pr35_slogan_overrides";

interface SloganSettings {
  enabled: Record<SloganSection, boolean>;
  translations: Partial<Record<SloganLang, string>>;
}

const DEFAULT_ENABLED: Record<SloganSection, boolean> = {
  home: true,
  login: true,
  register: true,
  onboarding: true,
  header: true,
  profile: true,
  partner_portal: true,
  war_room: true,
  splash: true,
  footer: true,
  seo: true,
  reports: true,
  emails: true,
  loading: true,
};

export function getSloganSettings(): SloganSettings {
  if (typeof window === "undefined") return { enabled: { ...DEFAULT_ENABLED }, translations: {} };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { enabled: { ...DEFAULT_ENABLED }, translations: {} };
    const parsed = JSON.parse(raw) as Partial<SloganSettings>;
    return {
      enabled: { ...DEFAULT_ENABLED, ...(parsed.enabled ?? {}) },
      translations: parsed.translations ?? {},
    };
  } catch {
    return { enabled: { ...DEFAULT_ENABLED }, translations: {} };
  }
}

export function setSloganSettings(next: SloganSettings) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* noop */
  }
}

export function getSlogan(lang: SloganLang = "ES"): string {
  const s = getSloganSettings();
  return s.translations[lang] ?? VERSESPORT_SLOGAN[lang];
}

export function isSloganEnabled(section: SloganSection): boolean {
  return getSloganSettings().enabled[section];
}

/** Composes the full SEO title — "VerseSport — Inteligencia Deportiva en Tiempo Real". */
export function brandedTitle(suffix?: string, lang: SloganLang = "ES"): string {
  const base = `VerseSport — ${getSlogan(lang)}`;
  return suffix ? `${suffix} · ${base}` : base;
}

export const PR35_DEFAULT_DESCRIPTION =
  "Plataforma global de segunda pantalla con telemetría, IA, predicciones, recompensas y analítica comercial en tiempo real.";

export const PR35_AUDIT = {
  pr: "PR-35",
  slogan: VERSESPORT_SLOGAN,
  sections: Object.keys(DEFAULT_ENABLED) as SloganSection[],
  notes:
    "Slogan layer added globally without removing existing branding (productName, tagline, legal footer preserved).",
};

export const PR35_QA_ITEMS: { id: string; label: string }[] = [
  { id: "home", label: "Slogan visible en homepage" },
  { id: "login", label: "Slogan visible en login y registro" },
  { id: "onboarding", label: "Slogan visible en onboarding" },
  { id: "partner", label: "Slogan visible en Partner Portal" },
  { id: "war", label: "Slogan visible en Business War Room" },
  { id: "pdf", label: "Slogan en reportes PDF" },
  { id: "seo", label: "Metadata SEO actualizada" },
  { id: "og", label: "Open Graph actualizado" },
  { id: "i18n", label: "Soporte ES/EN/PT" },
  { id: "tsc", label: "tsc --noEmit = 0 errores" },
];